import api from './api'
import { toCamelCase } from '@/utils/transform'

/** How a bulk drop of loose documents should be split into deals.
 *  `auto` lets the engine decide on extracted evidence; the other two are the
 *  operator overriding that judgment. Emails are always one record each. */
export type GroupingMode = 'auto' | 'single' | 'separate'

/** One extracted field with provenance from the deterministic parse. */
export interface ParsedField {
  field: string
  value: unknown
  raw: string
  source: string
  lineNo?: number | null
  label?: string | null
  method: string           // labeled_line | subject_pattern | shorthand_pattern | derived | workbook_anchor
  normalization?: string | null
  flags?: string[]
  cellRef?: string | null
  anchor?: SourceAnchor | null
}

export interface ParseConflict {
  field: string
  values: { value: unknown; raw?: string; source: string; lineNo?: number | null; method?: string }[]
  resolution: string
}

/** A labeled workbook line the ratified template does not map (model change). */
export interface BaselineDiffEntry {
  metric: string
  sheet: string
  label: string
  labelCell: string
  valueCell: string
  valuePreview?: string
  exact?: boolean
}

/** One entry an archive did not give up cleanly (skipped, unreadable, or encrypted). */
export interface ArchiveIssueView {
  path: string
  /** unsafe_path | special_file | encrypted | corrupt | oversize | compression_ratio | entry_cap |
   *  member_cap | total_cap | empty_archive | nested_archive | duplicate | empty_file */
  code: string
  reason: string
}

/** What happened to one ZIP in a package (parsing.archives.ArchiveReport). `ok` means every
 *  entry that mattered was read; `partial` means a person must look; `refused` means nothing
 *  could be read. */
export interface ArchiveReportView {
  archive: string
  depth: number
  status: 'ok' | 'partial' | 'refused'
  entries: number
  processed: number
  ignored: number
  skipped: ArchiveIssueView[]
  failed: ArchiveIssueView[]
  encrypted: ArchiveIssueView[]
  members: { path: string; provenance: string; sha256: string; size: number; renamedFrom?: string | null }[]
  truncated: boolean
  truncatedReason?: string | null
  refusedReason?: string | null
  refusedCode?: string | null
  /** The one honest line, e.g. "ZIP: 14 files processed, 2 skipped (unsafe path ×2), 1 encrypted — needs password". */
  summary: string
}

export interface AttachmentReport {
  filename: string
  sha256?: string
  sizeKb?: number
  kind: string
  /** parsed_template | parsed_freeform | parsed_text | parsed_ocr | parsed_archive |
   *  needs_model_onboarding | template_ambiguous | unparsed | error — each one's label
   *  lives in components/intake/AttachmentStatusBadge.tsx. */
  status: string
  /** Set on an archive's own row. */
  archive?: ArchiveReportView | null
  /** Set on a file read out of an archive: which archive and where in it. */
  archiveMember?: { path: string; name: string; archive?: string | null; depth: number; renamedFrom?: string | null } | null
  detail: string
  fields?: ParsedField[]
  template?: { id: string; name: string; client: string; version: number } | null
  candidateTemplates?: { id: string; name: string; client: string; version: number }[]
  onboardingDraftAvailable?: boolean
  baselineDiff?: BaselineDiffEntry[]
  warnings?: string[]
  parsed?: boolean
}

/** A field a later thread message would add to the converted loan (empty there today). */
export interface ThreadLoanAddition {
  field: string
  loanField: string
  value: unknown
  current?: unknown
  source?: string
}

/** A field where the new message disagrees with the loan as it stands. */
export interface ThreadLoanConflict {
  field: string
  loanField: string
  incoming: unknown
  current: unknown
  source?: string
  basis?: string
}

/** What a thread message states against its converted loan (rules R5/R6).
 *
 *  Figures the deal's own sender states afresh in a reply reach the deal on
 *  arrival (`applied[field].automatic`); a figure that contests a value a person
 *  confirmed, an identity field, or anything from a heuristic link waits and is
 *  listed in the record's `pendingRevisions`. */
export interface ThreadLoanUpdates {
  loanId: string
  loanPropertyName?: string
  additions: ThreadLoanAddition[]
  conflicts: ThreadLoanConflict[]
  /** `separated`: a person made this email its own deal (`separatedAs`); it
   *  revises nothing on `loanId` (nd-008). */
  status: 'pending' | 'applied' | 'separated'
  separatedAs?: string
  applied?: Record<string, {
    from: unknown; to: unknown; loanField: string; by?: string; at?: string
    /** Carried onto the deal on arrival, not by a person. */
    automatic?: boolean
  }>
  /** Pending values a person declined — the deal kept its own. */
  kept?: Record<string, { kept: unknown; declined: unknown; by?: string; at?: string }>
  /** Fields the message only restated from the quoted chain or subject line. */
  restated?: string[]
  /** Figures a NEWER email already put on the deal: this older message is
   *  their history and changed nothing. */
  superseded?: { field: string; incoming?: unknown; current?: unknown; why: string }[]
  /** Derived fields the recompute moved after the update. */
  recomputed?: string[]
  note?: string
  /** The deal was killed after this email was linked to it — nothing applies. */
  loanKilled?: boolean
}

/** One figure a later email states that waits for a person before it may
 *  replace the deal's value (rule R5). Read back onto the deal as a flag. */
export interface PendingRevision {
  ingestionId?: string
  field: string
  loanField?: string
  current: unknown
  incoming: unknown
  source?: string
  subject?: string
  from?: string
  receivedAt?: string | null
  /** Why it waits, in the reviewer's language. */
  why: string
  /** The lender's own draft terms, not a revision from the broker. */
  lenderDraft?: boolean
}

/** A killed deal a new request matches. The request became a new deal; this
 *  says which dead one it resembles, so reopening that is still a choice. */
export interface ClosedDealMatch {
  loanId: string
  /** `property_name` arrives as `property` through toCamelCase. */
  property?: string
  basis: string
  reason: string
  note: string
}

/** A field a re-ingested source would add to the matched deal (empty there today). */
export interface ReingestAddition {
  field: string
  loanField: string
  value: unknown
  current?: unknown
  source?: string
  incomingSource?: string
}

/** A field where a re-ingested source disagrees with the matched deal. */
export interface ReingestConflict {
  field: string
  loanField: string
  incoming: unknown
  current: unknown
  source?: string
  incomingSource?: string
  currentSource?: string
  basis?: string
}

/** The field-level diff between a re-ingested record and the deal it matched by
 *  Control ID (register D1). Nothing is overwritten — a human resolves each field
 *  in the Data Intelligence Review, newest-wins by default. */
export interface ReingestUpdates {
  loanId: string
  loanPropertyName?: string
  controlId?: string | null
  matchBasis?: string
  currentSource?: string
  additions: ReingestAddition[]
  conflicts: ReingestConflict[]
  status: 'pending' | 'applied'
  applied?: Record<string, { from: unknown; to: unknown; choice: 'new' | 'current'; loanField: string; source?: string; by?: string; at?: string }>
  note?: string
}

/** Thread linkage evidence for an ingested email. */
export interface ThreadInfo {
  messageId?: string | null
  inReplyTo?: string | null
  references?: string[]
  subjectNormalized?: string
  threadGroupId: string
  linkedTo?: string | null
  linkBasis?: 'in_reply_to_header' | 'references_header' | 'subject_participants' | null
  priorMessages: number
  warnings?: string[]
  loanUpdates?: ThreadLoanUpdates | null
}

/** How the deal name (property_name) was derived — register A2. */
export interface DealNameReport {
  value: string
  basis: 'broker_parenthetical' | 'portfolio' | 'property' | 'subject_fallback' | 'human_resolved'
  lowConfidence: boolean
  subRecords?: string[]
  flags?: string[]
  /** Subject-derived name suggestions offered in the Data Intelligence Review (never auto-applied). */
  candidates?: string[]
  resolvedBy?: string
  resolvedFrom?: string | null
}

/** Why this record was NOT turned into a deal on arrival.
 *
 *  A promotion that quietly does not happen is invisible work: the record sits
 *  in the queue looking like every other one and nobody knows it is waiting on
 *  them. The reason is stated on the record so the screen can say it. */

/** The killed deal an identical re-upload's existing record became (C18/C30). */
export interface DuplicateMatchedDeal {
  loanId: string
  propertyName: string | null
  dealStatus: 'dead'
  deadAt: string | null
  note: string
}

/** One entry of POST /ingestion/parse-files. */
export interface UploadResult {
  duplicate: boolean
  record: Ingestion
  /** Present only when the duplicate's deal has been killed. */
  matchedDeal?: DuplicateMatchedDeal
}

export interface PromotionHoldReason {
  reason: string
  detail: string
  /** What the reviewer does to release it. */
  clearsWhen: string
  heldAt: string
  /** The existing deal the record was held against, when there is one. */
  loanId?: string
  /** That deal has since been killed — reopen it or create a new deal. */
  loanKilled?: boolean
}

/** The record's hold. Its own fields are the FIRST reason written; `reasons` is
 *  every active reason, that one included (a record can be held for several at
 *  once — read them with `holdReasons`, components/intake/holdReasons.ts). Absent
 *  on a hold stored before reasons accumulated, which is its one reason. */
export interface PromotionHold extends PromotionHoldReason {
  reasons?: PromotionHoldReason[]
}

/** A record off the pipeline with no reason saved, served beside it and never
 *  stored. Records held before hold reasons were recorded carry none; the
 *  screen says the reason is unknown rather than drawing nothing or guessing. */
export interface HoldState {
  state: 'reason_not_recorded'
  detail: string
}

/** One field the engine now reads differently from the stored value.
 *
 *  A re-parse REPLACES a value somebody may have chosen on purpose, so it is
 *  offered rather than applied. `humanDecided` marks the ones where a person
 *  already answered: their choice outranks any reading of the document, and the
 *  screen has to say so rather than quietly offer to replace it. */
export interface ReparseProposal {
  field: string
  /** 'replacement' offers a value to choose; 'unsupported' means the engine can
   *  no longer identify anyone for this field and is asking a person to look. */
  kind?: 'replacement' | 'unsupported'
  current: unknown
  incoming: unknown
  currentSource?: string
  incomingSource?: string
  /** The exact stored document and locator supporting each side. A legacy
   * proposal may omit these; the UI states that limitation instead of
   * presenting a generic engine label as a source. */
  currentEvidence?: ReparseEvidence | null
  incomingEvidence?: ReparseEvidence | null
  humanDecided?: boolean
  decidedBy?: string | null
  priorDecision?: ReparsePriorDecision | null
  basis?: string
}

export interface ReparseCalcInput {
  field: string
  value: unknown
  source?: unknown
}

export interface ReparseEvidence {
  /** 'computed' — no document exists to cite; this is a ratio of other
   *  fields (ltv, ltc, debt yield, ...). `formula`/`inputs` carry the real
   *  numbers used, not just the field names, so the result is verifiable. */
  availability: 'recorded' | 'unavailable' | 'not_found' | 'computed'
  source?: string | null
  sourceSha256?: string | null
  raw?: string | null
  label?: string | null
  lineNo?: number | null
  page?: number | null
  cellRef?: string | null
  method?: string | null
  normalization?: string | null
  locator?: SourceAnchor | null
  anchor?: SourceAnchor | null
  flags?: string[]
  corroboration?: unknown[]
  sourceFiles?: string[]
  formula?: string | null
  inputs?: ReparseCalcInput[] | null
  detail?: string | null
}

export interface ReparsePriorDecision {
  method?: string | null
  by?: string | null
  at?: string | null
  reason?: string | null
  evidence?: ReparseEvidence | null
}

export interface ReparseUpdates {
  engine?: string
  proposedAt?: string
  status: string
  proposals: ReparseProposal[]
  sourceFiles?: string[]
  resolved?: Record<string, {
    choice: string
    from?: unknown
    to?: unknown
    by?: string
    at?: string
    source?: string
    evidence?: { current?: ReparseEvidence | null; incoming?: ReparseEvidence | null }
  }>
  note?: string
}

/** One write the reconciliation loop made (or, in a preview, would make). */
export interface ReconcileWrite {
  pass: number
  step: string
  field: string
  from?: unknown
  to?: unknown
  source?: string | null
}

/** Something only a person can settle — a value a person decided, rival
 *  counterparties, a workbook nobody can attribute. Never written by the loop. */
export interface ReconcileNeedsPerson {
  field?: string | null
  kind: string
  detail: string
  workbook?: string | null
  current?: unknown
  incoming?: unknown
  decidedBy?: string | null
}

export interface ReconcileCoverage {
  populated: number
  total: number
  onRecordNotOnDeal: { field: string; value?: unknown; source?: string | null }[]
  conflicts: { field: string }[]
  attachments: { filename?: string | null; status?: string | null; fieldsRead: number; unrecognisedLabels: number }[]
  workbooks: { filename: string; role?: string | null; mappedCells: number; importedCells: number; mappingStatus?: string }[]
  sponsorModelFields: string[]
}

export interface ReconcileDeal {
  ingestionId: string
  loanId: string
  /** Wall-clock cost of this deal — what makes the desk's real cost visible. */
  seconds?: number
  subject?: string | null
  /** `property_name` on the wire; `toCamelCase` maps it to `property`. */
  property?: string | null
  passes: number
  converged: boolean
  writes: ReconcileWrite[]
  writeCount: number
  counts: Record<string, number>
  needsPerson: ReconcileNeedsPerson[]
  failed: string[]
  coverage: ReconcileCoverage
}

export interface ReconcileReupload {
  ingestionId: string
  loanId: string
  /** `property_name` on the wire; `toCamelCase` maps it to `property`. */
  property?: string | null
  file?: string | null
  held: boolean
  detail: string
}

export interface ReconcileResponse {
  applied: boolean
  engine: string
  examined: number
  deals: ReconcileDeal[]
  totals: Record<string, number>
  skipped: Record<string, number>
  failed: { ingestionId: string; loanId?: string | null; detail: string }[]
  /** Deals whose stored package could not be re-read. Their re-parse was
   *  skipped, so an apply takes back figures it cannot restore — the operator
   *  re-uploads first (nd-001). `held`: the automated sweep did not reconcile
   *  the deal against its package. */
  needsReupload?: ReconcileReupload[]
  /** Whether the whole selection was reached. */
  complete: boolean
  /** Intake ids this call never started — the next call's input. Not failures. */
  remaining: string[]
  elapsedSeconds: number
  note: string
}

/** One standing sweep of the reconciliation loop against the whole live desk —
 *  scheduled, or run now. See `app.core.desk_reconciliation_scheduler`. */
export interface DeskReconciliationRun {
  id: string
  createdAt: string
  completedAt: string | null
  status: 'running' | 'complete' | 'failed' | 'partial' | 'cancelled'
  triggeredBy: string
  detail: string | null
  dealsExamined: number
  writes: number
  needsPerson: number
  converged: number
  datapointsDiscovered: number
  datapointsAutoAccepted: number
  fieldsPopulated: number | null
  fieldsTotal: number | null
  engineMisses: number | null
  fieldsPopulatedBefore: number | null
  engineMissesBefore: number | null
  /** Same shape a manual reconcile already returns — renders in `ReconcileReport` unchanged. */
  report: ReconcileResponse | null
  census: Record<string, unknown> | null
}

/** Why one unpopulated dossier field is still empty. */
export type GapResolution =
  | 'value_available'
  | 'label_seen_unparsed'
  | 'derivable_when'
  | 'needs_review'
  | 'absent_from_package'
  | 'desk_assigned'
  | 'not_applicable'
  | 'no_search_route'

/** One reading of a gap's value, found in a source, with where it was read. */
export interface GapFinding {
  value: unknown
  /** Could the desk write it? Per-field acceptance, the same gate projection uses. */
  admissible: boolean
  /** Does it belong to THIS field, or to a family this field is in? */
  precise: boolean
  raw?: string | null
  doc?: { filename?: string | null; sha256?: string | null; kind?: string | null } | null
  at?: {
    kind?: string | null
    sheet?: string | null
    cell?: string | null
    lineNo?: number | null
    page?: number | null
    metric?: string | null
  } | null
  methodLabel?: string | null
  label?: string | null
}

export interface FieldGap {
  field: string
  page: string
  group: string
  label?: string | null
  /** The coverage status underneath — `missing`, `conflict`, `in_unread_model`. */
  status: string
  resolution: GapResolution
  /** The sentence that justifies the resolution. */
  why: string
  found: GapFinding[]
  /** Which sources were read, and what happened in each. Present even when
   *  nothing was found — a search that comes back empty still says where it looked. */
  searched: { doc?: { filename?: string | null } | null; outcome: string; detail: string }[]
  understanding: {
    label: string
    kind?: string | null
    unit?: string | null
    aliases: string[]
    workbookVocabulary: string[]
    derivable: { formula: string; inputs: string[]; missingInputs: string[]; ready: boolean }[]
    feeds: string[]
  }
}

export interface GapCensus {
  ingestionId: string
  loanId?: string | null
  pages: { page: string; groups: string[]; total: number; populated: number; counts: Record<string, number> }[]
  total: number
  /** Catalogue fields the intake RECORD's projection holds. */
  populated: number
  /** Catalogue fields the promoted deal holds — the pipeline's own
   *  completeness measure for it. Null while the record is not a deal. */
  populatedOnDeal?: number | null
  gaps: FieldGap[]
  resolutions: Partial<Record<GapResolution, number>>
  /** The gate: datapoints a document holds and the dossier does not show. */
  engineMisses: number
  searched: boolean
  searchError?: string | null
  elapsedMs: number
}

export interface GapCensusDesk {
  deals: GapCensus[]
  remaining: string[]
  complete: boolean
  skipped: Record<string, string[]>
  populated: number
  catalogue: number
  totals: Partial<Record<GapResolution, number>>
  engineMisses: number
  elapsedSeconds: number
}

export interface BrokerResolutionCandidate {
  id?: string | null
  name: string
  email?: string | null
  emailDomain?: string | null
  firmId?: string | null
  firmName?: string | null
  /** An existing contact's firm, by name (sent with a contact the resolver matched). */
  firm?: string | null
  score?: number | null
  /** Why the resolver offered it, e.g. `email_domain_name_affinity` (a brokerage the
   *  sender's email domain echoes), `exact_name_in_suggested_firm`, `proposed_new`, `sender`. */
  basis?: string
  requiresConfirmation?: boolean
}

export interface BrokerResolution {
  brokerId?: string | null
  brokerFirmId?: string | null
  firmStatus: 'linked' | 'created' | 'candidates' | 'conflict' | 'blocked' | 'none'
  contactStatus: 'linked' | 'created' | 'candidates' | 'conflict' | 'blocked' | 'none'
  firmBasis?: string | null
  contactBasis?: string | null
  candidates: {
    firm: BrokerResolutionCandidate[]
    contact: BrokerResolutionCandidate[]
  }
  /** What intake created for this record, when it created anything. */
  created?: { firm?: string | null; contact?: string | null } | null
  notes?: string[]
}

/** One intake record whose broker intake could not settle on its own
 *  (GET /ingestion/broker-review). Since 4 Nov 2026 intake creates and links
 *  brokers automatically; what is listed here is a conflict, several matches,
 *  a raw sender it never files as a broker, a record parsed under the old
 *  confirmation rule, or a workspace with automatic creation switched off. */
export interface BrokerReviewItem {
  ingestionId: string
  propertyName?: string | null
  brokerName?: string | null
  sourceEmail?: string | null
  resolution: BrokerResolution
  createdAt?: string | null
}

export interface BrokerReviewQueue {
  count: number
  items: BrokerReviewItem[]
}

/** POST /ingestion/broker-review/confirm-batch: each selected record's own
 *  proposal confirmed, one record at a time. A record with no single
 *  proposal, or one that needs a person's choice, is in `failed` with why. */
export interface BrokerBatchResult {
  confirmed: { ingestionId: string; brokerId?: string | null; brokerFirmId?: string | null; alsoLinked: string[] }[]
  failed: { ingestionId: string; code: string; reason: string }[]
  skipped: string[]
}

/** `parse_report.ocr_in_progress` (api/v1/ingestion.py `mark_ocr_in_progress`): a scan the
 *  one-request OCR budget cut short, being finished behind the response. `incomplete` is the
 *  last word when the pages still owed will not be read (the engine's 40-page cap, or a read
 *  that failed): "OCR incomplete: N pages not read — confirm by eye". */
export interface OcrInProgress {
  status: 'queued' | 'running' | 'incomplete'
  /** The sentence the record shows, e.g. "OCR in progress: 2 pages remaining". */
  note: string
  pagesRemaining: number
  /** Per attachment still owed pages (`sha256` is the stored original's, as on `rawFilePaths`). */
  attachments?: { filename?: string | null; sha256?: string | null; pagesRemaining: number }[]
  since?: string
}

export interface ParseReport {
  engine: string
  doctrine?: string
  /** Set while a scan's remaining pages are read behind the response, and left as
   *  `incomplete` when they could not be. Absent once every page was read. */
  ocrInProgress?: OcrInProgress | null
  promotionHold?: PromotionHold
  /** Differences a re-parse found, awaiting a person's decision. */
  reparse?: ReparseUpdates
  dealName?: DealNameReport
  fields: ParsedField[]
  conflicts: ParseConflict[]
  review?: ReviewReport
  /** Structure terms stated by guidance-role documents (sale OM, SOW) —
   *  market color, deliberately kept out of the deal's own fields. */
  guidanceFields?: { field: string; value: unknown; raw?: string; source: string; lineNo?: number | null; role?: string; note?: string }[]
  unmatchedLines?: { source: string; lineNo?: number; label?: string; text: string; reason: string }[]
  unmatchedTruncated?: number
  containerWarnings?: string[]
  attachments?: AttachmentReport[]
  /** One report per ZIP in the package (nested ones included): what was read, and what was not. */
  archives?: ArchiveReportView[]
  coverage?: number
  coverageBasis?: string
  screenInputSelection?: {
    value?: string | null
    cashFlow?: string | null
    basis?: string
    notes?: string[]
  }
  thread?: ThreadInfo | null
  reingest?: ReingestUpdates | null
  /** Figures a later email states that wait for a person (rule R5). */
  pendingRevisions?: PendingRevision[]
  /** Killed deals this request matches (it became a new deal regardless). */
  closedDealMatches?: ClosedDealMatch[]
  documentGrouping?: DocumentGrouping | null
  /** Governed Q1 resolution: misses are suggestions until a person confirms. */
  brokerResolution?: BrokerResolution | null
}

/** How a bulk drop of loose documents was partitioned into deals. Emails
 *  delineate themselves and carry no grouping block. */
export interface DocumentGrouping {
  mode: GroupingMode
  group: number
  groupsInRequest: number
  files: string[]
  basis: string
  notes?: string[]
  doctrine?: string
}

export interface ScreenCheck {
  name: string
  outcome: 'PASS' | 'EXCEPTION' | 'REFER' | 'MISSING_INPUT'
  detail: string
  missingField?: string | null
  fillHint?: string | null
}

export interface ScreenRun {
  program: string
  programName: string
  rulesVersion: string
  rulesSha256: string
  outcome: 'PASS' | 'EXCEPTION' | 'INCOMPLETE'
  inputs: { field: string; value: unknown; source: string; selectedFrom?: string | null }[]
  checks: ScreenCheck[]
  missingInputs: { check: string; field?: string | null; fillHint?: string | null }[]
  referrals: string[]
}

export interface ScreenResults {
  status: 'run' | 'not_run'
  programs?: string[]
  runs?: ScreenRun[]
  reason?: string
  missing?: { field: string; why: string; fillHint: string }[]
}

export interface EmailEnvelope {
  container: 'eml' | 'msg'
  fromEmail?: string | null
  fromName?: string | null
  to?: string[]
  cc?: string[]
  subject?: string | null
  sentAt?: string | null
  messageId?: string | null
  inReplyTo?: string | null
  references?: string[]
  bodySource?: string
}

/** A stored source file on an ingestion record (original email / attachment). */
export interface SourceFileRef {
  filename: string
  kind: 'email_source' | 'attachment' | 'document'
  sha256: string
}

/** In-platform preview of a stored source file. */
export interface RerunResult {
  ingestion_id: string
  reran_from: string
  persisted: false
  changed_count: number
  unchanged_count: number
  changed: { field: string; before: unknown; after: unknown }[]
  fresh_extraction_notes: string | null
  fresh_attachment_statuses: { filename: string; status: string }[]
}

export interface InfoRequest {
  ingestion_id: string
  deal_name: string
  addressed_to: string | null
  text: string
  counts: { missing_required: number; missing_optional: number; conflicts: number; in_unread_model: number }
  routing: 'internal'
  sends: false
}

export interface SourcePreview {
  kind: 'email' | 'xlsx' | 'pdf' | 'text' | 'image' | 'binary'
  filename: string
  envelope?: EmailEnvelope & { fromEmail?: string | null }
  text?: string
  /** Sanitized original text/html part, when the message carried one. */
  html?: string | null
  truncated?: boolean
  attachments?: string[]
  warnings?: string[]
  sheets?: { name: string; rows: (string | number | boolean | null)[][] }[]
  detail?: string
  /** PDF extracted-text tab: 1-based line where each page starts. */
  pageStartLines?: number[]
  /** Register C1 — MIME type for an image preview, so the blob is
   *  reconstructed as the format it actually is. */
  contentType?: string
}

/** A stored document this record was built from, addressed by content hash —
 *  every source-file endpoint is keyed by sha256, so a filename alone cannot
 *  open anything. */
export interface DocRef {
  filename: string
  sha256?: string | null
  kind: string
  role?: string | null
  status?: string | null
}

/** Where inside its document a value sits. */
export interface SourceLocator {
  kind?: string | null
  lineNo?: number | null
  page?: number | null
  sheet?: string | null
  cell?: string | null
  region?: string | null
  ocr?: boolean | null
}

/** Where one value came from, addressably: which document, where inside it,
 *  under what label, and how it was read. */
export interface Provenance {
  doc: DocRef
  locator: SourceLocator
  snippet?: string | null
  label?: string | null
  method?: string | null
  /** The read method as a sentence — "read from a labelled line". */
  methodLabel?: string | null
  normalization?: string | null
  /** 'sha256' when the document was addressed by hash; 'filename' is a guess. */
  resolvedBy?: 'sha256' | 'filename'
}

/** Why a datapoint needs a person, from a closed vocabulary. */
export interface ReviewReason {
  code: string
  text: string
  flag?: string
}

/** Another statement of the same datapoint somewhere in the package. */
export interface ReviewCandidate {
  value: unknown
  raw?: string | null
  /** corroborates | conflicts | differs | superseded | guidance | alternative | located */
  relation?: string
  /** The stated reason it does or does not govern. */
  why?: string
  provenance?: Provenance
  flags?: string[]
  /** Retained for the pre-evidence shape. */
  source?: string
  lineNo?: number | null
}

/** One document examined for a datapoint, and what happened there. */
export interface SearchedDoc {
  doc: DocRef
  outcome: 'found' | 'no_label_match' | 'label_seen_unparsed' | 'unread_model'
    | 'unreadable' | 'not_searched'
  detail: string
}

/** What confirming a value would agree or disagree with. */
export interface ConsistencyCheck {
  check: string
  verdict: 'agrees' | 'contradicts'
  detail: string
  implied?: number
}

/** One needed datapoint on the Data Intelligence Review checklist. Every record carries
 *  every datapoint with an explicit status — nothing is silently absent — and
 *  every item carries the same evidence block whatever its status. */
export interface ReviewItem {
  field: string
  label: string
  status: 'found' | 'flagged' | 'conflict' | 'missing' | 'derivable' | 'resolved'
  trace?: string
  inputs?: { field: string; label?: string; value: unknown; source?: string; anchor?: SourceAnchor | null; provenance?: Provenance | null }[]
  value?: unknown
  flags?: string[]
  source?: string
  anchor?: SourceAnchor | null
  /** Where this value came from. Null only when nothing bound. */
  provenance?: Provenance | null
  /** Why it needs a person, in plain English. */
  reasons?: ReviewReason[]
  candidates?: ReviewCandidate[]
  /** Which documents were examined and what happened in each. */
  searched?: SearchedDoc[]
  consistency?: ConsistencyCheck[]
  evidence?: { kind: 'unmatched' | 'guidance'; source?: string; lineNo?: number | null; label?: string; text?: string; value?: unknown; note?: string; reason?: string; provenance?: Provenance }[]
  basis?: string
  /** Set once a person has settled this datapoint. */
  decision?: ReviewDecision | null
  resolvedBy?: string
  resolvedAt?: string
}

/** How a reviewer settled one datapoint. */
export type ReviewDecisionKind = 'confirm' | 'correct' | 'accept_located' | 'unavailable'

/** A decision recorded on a review item. */
export interface ReviewDecision {
  kind: ReviewDecisionKind
  by: string
  at: string
  reason?: string | null
  note?: string | null
  provenance?: Provenance | null
  factId?: string
  previous?: { value?: unknown; status?: string; provenance?: Provenance | null }
}

export interface LocateInput {
  field?: string
  fields?: string[]
  sha256?: string
  sheet?: string
  pageFrom?: number
  pageTo?: number
  cell?: string
  lineNo?: number
}

/** One value the engine found where it was pointed. */
export interface LocatedCandidate {
  value: unknown
  raw?: string | null
  label?: string | null
  method: string
  provenance: Provenance
  /** The engine's case for this reading, in words. */
  why: string[]
}

export interface LocateResult {
  mode: 'sweep' | 'scan' | 'scope' | 'pick'
  elapsedMs: number
  basis?: string
  /** Present when one datapoint was asked about. */
  field?: string
  candidates?: LocatedCandidate[]
  searched?: SearchedDoc[]
  results: { field: string; candidates: LocatedCandidate[]; searched: SearchedDoc[] }[]
}

export interface ReviewReport {
  items: ReviewItem[]
  counts: Record<string, number>
  open: number
  needsReview: boolean
  basis?: string
  /** What the evidence block guarantees. */
  evidenceBasis?: string
  /** Every document this record was built from. */
  documents?: DocRef[]
}

/** Live parse-progress snapshot (polled while Parse & Extract runs). */
export interface ParseProgressSnapshot {
  stages: { label: string; detail?: string | null; index?: number | null; total?: number | null; at: number }[]
  done: boolean
  error?: string | null
  totalFiles: number
  elapsedS: number
}

/** Click-to-source anchor carried on every extracted field (WP-J). */
export interface SourceAnchor {
  kind?: 'email_body' | 'email_subject' | 'pdf' | 'docx' | 'pptx' | 'csv' | 'xlsx' | 'text'
  lineNo?: number | null
  snippet?: string
  page?: number
  cell?: string
  region?: 'quoted'
  /** Content hash of the stored file this anchor addresses. The viewer is
   *  sha-keyed, so without this the caller has to guess the file by name. */
  sha256?: string
}

/** A deal-ingestion record as returned by the backend (camel-cased). */
export interface Ingestion {
  id: string
  sourceType: 'email' | 'pdf' | 'excel' | 'word' | 'manual'
  sourceEmail?: string
  sourceSubject?: string
  sourceBody?: string
  receivedAt?: string
  brokerName?: string
  brokerId?: string
  brokerFirmId?: string
  extractedPropertyName?: string
  extractedAssetType?: string
  extractedLocation?: string
  extractedBorrower?: string
  extractedSponsor?: string
  extractedLoanAmount?: number
  extractedLtv?: number
  extractedDscr?: number
  extractedOccupancy?: number
  extractedNoi?: number
  extractedCapRate?: number
  extractedUnitsOrSf?: string
  extractedFields?: Record<string, unknown>
  extractionConfidence?: number
  aiMandateFit?: string
  aiSuggestedProduct?: string
  aiPriority?: string
  aiRiskFlags?: string[]
  aiProcessingMode?: string
  fieldMapping?: Record<string, { sourceDoc?: string; cellRef?: string }>
  attachments?: { filename: string; fileType?: string; sizeKb?: number; parsed?: boolean; sha256?: string }[]
  status: 'new' | 'reviewed' | 'converted' | 'declined' | 'deferred'
  reviewNotes?: string
  reviewedBy?: string
  reviewedAt?: string
  convertedToLoanId?: string
  createdAt?: string
  extractionNotes?: string
  contentSha256?: string
  rawFilePaths?: SourceFileRef[]
  emailEnvelope?: EmailEnvelope | null
  parseReport?: ParseReport | null
  holdState?: HoldState | null
  screenResults?: ScreenResults | null
  calculationTrace?: Record<string, { value: number; formula: string; inputs: Record<string, { value: unknown; source: string }> }>
}

/** One existing deal an intake record may be a re-upload of (register A8). */
export interface MatchCandidate {
  loanId: string
  /** The deal's name. The API sends `property_name`, which toCamelCase maps to
   *  `property` — reading `propertyName` printed the loan id twice (REG-07). */
  property?: string | null
  controlId?: string | null
  location?: string | null
  loanAmount?: number | null
  /** How the match was made: control_id_exact | property_name_exact |
   *  address_exact | address_fuzzy | sponsor_fuzzy. */
  basis: string
  score: number
}


/** Pre-commit duplicate preview: existing deals this record may attach to.
 *  Nothing is merged server-side — the operator decides attach vs create new. */
export interface MatchPreview {
  ingestionId: string
  controlId?: string | null
  candidates: MatchCandidate[]
  note: string
}

/** How a dossier field came to have (or not have) a value. Mirrors the FIVE
 *  server statuses; `stated`/`conflict` map onto the shared field-state
 *  vocabulary's `extracted`/`conflicted` at render time.
 *
 *  `in_unread_model` was emitted by the server and absent here, so every render
 *  site fell through to `missing` — a field an attached workbook plainly states
 *  displayed as absent. */
export type DossierFieldStatus =
  | 'stated'
  | 'derived'
  | 'conflict'
  | 'missing'
  | 'in_unread_model'
  /** A reviewer read the package and confirmed the value is not in it. Distinct
   *  from `missing`, which means nobody has looked yet — the decision wrote no
   *  value (correctly, there is none) and coverage read only values, so the
   *  dossier went on asking a question a person had already answered. */
  | 'not_derivable_from_package'
  /** An assumption or a default — a country from the state code, the triage
   *  priority, a market-convention payment frequency. Shown for a person to
   *  accept; never counted as populated. */
  | 'suggested'
  /** A person's decision (Data Intelligence Review) — who, when and why (nd3-50). Counted
   *  as populated; never read as a document's statement. */
  | 'confirmed'

export interface DossierField {
  field: string
  label: string
  kind: string
  unit?: string | null
  required: boolean
  status: DossierFieldStatus
  value?: unknown
  /** Confirmed only: who decided it, when, and why. */
  confirmation?: { by?: string | null; at?: string | null; rationale?: string | null; via?: string | null; kind?: string | null }
  provenance?: {
    sourceDoc?: string | null
    cellRef?: string | null
    method?: string | null
    lineNo?: number | string | null
    /** Where in the file the value was read: the page and line of a PDF, the
     *  line of an email body (and whether it was quoted), a workbook cell. The
     *  server has sent it on every stated field; the type omitted it, so no
     *  screen could open the original at the place. */
    locator?: SourceAnchor | null
    /** Content hash of the stored file the value was read from. */
    sourceSha256?: string | null
    /** A split part (city out of the location) names the field it was cut from. */
    inheritedFrom?: string | null
  }
  /** Stated only: the intake records that state this value. The dossier
   *  merges the deal's whole group, a held email a person has not accepted
   *  included (R5), so this tells the deal's own reading from another's. */
  statedBy?: string[]
  /** Derived only: the identity used, and the stated inputs it consumed. */
  formula?: string | null
  inputs?: Array<{ field: string; label: string; value: number }>
  /** Suggested only: why the platform offers this value. */
  basis?: string | null
  /** A stated metric the platform also computes, where the two differ — for
   *  information, never a choice (rules R3 / R4). */
  variance?: { stated: unknown; computed: unknown; formula?: string | null; informational?: boolean } | null
  note?: string
  candidates?: Array<Record<string, unknown>>
  /** Why an empty field is empty — desk work, external lookup, or a package miss. */
  gapKind?: 'desk_assigned' | 'external' | 'not_applicable' | 'reviewer_confirmed' | 'absent'
    /** Nothing read states it, but an attachment could not be read (a scan with OCR off). */
    | 'unread_attachment'
  fillHint?: string
  /**
   * `in_unread_model` only: WHERE an attached workbook the engine has not been
   * onboarded for states this value — the file, the sheet!cell its survey
   * found, the label beside it and how many candidate cells offered it. The
   * server has emitted these since the survey shipped; this type omitted them,
   * so every render showed the value as missing and sent the reviewer hunting
   * for a document already in the email (UI-06).
   */
  sourceModel?: string | null
  modelCell?: string | null
  modelLabel?: string | null
  candidatesInModel?: number | null
  /** `missing` only: attached models not yet surveyed that may state this. */
  mayBeInUnreadModel?: string[] | null
}

export interface DossierGroup {
  group: string
  fields: DossierField[]
  counts: Partial<Record<DossierFieldStatus, number>>
  populated: number
  total: number
}

/** One thing standing between this record and a processable loan file.
 *  `derivable` means a calculation already answers it — confirm, don't chase. */
export interface DossierBlocker {
  kind: 'screen' | 'required_field' | 'document'
  field?: string | null
  why: string
  fillHint?: string | null
  severity: 'blocking' | 'derivable'
  derivedValue?: number
  derivedField?: string
}

/** One source this deal was built from — an email and whatever it carried. */
export interface DossierSource {
  position: number
  ingestionId: string
  receivedAt: string | null
  sourceType: string
  subject: string | null
  fromName?: string | null
  fromEmail?: string | null
  to: string[]
  cc: string[]
  attachments: Array<{ filename: string; status?: string | null; kind?: string | null; sizeKb?: number | null }>
  isCurrent: boolean
}

/** A datapoint a later message actually revised, with what it replaced. */
export interface DossierRevision {
  field: string
  value: unknown
  receivedAt: string | null
  subject: string | null
  superseded: Array<{
    value: unknown
    receivedAt: string | null
    subject: string | null
    quoted: boolean
  }>
}

export interface DossierCoverage {
  ingestionId: string
  groups: DossierGroup[]
  counts: Partial<Record<DossierFieldStatus, number>>
  populated: number
  total: number
  /** Empty fields the desk or an external lookup must fill — not extraction misses. */
  needsManual?: number
  /** Catalogue minus desk-assigned / external / not-applicable empties. */
  packageTotal?: number
  blockers: { items: DossierBlocker[]; blocking: number; derivable: number }
  /** Every source, oldest first. A reply revises some datapoints and leaves
   *  the rest alone, so the dossier is the whole thread's answer — this is
   *  where each value came from and when. */
  sources?: DossierSource[]
  revisions?: DossierRevision[]
  /** Columns the record offered and per-field acceptance turned away.
   *
   *  The server has emitted these since acceptance shipped and this interface
   *  had no key for them, so they were dropped at the boundary — reproducing
   *  the silent drop the whole control exists to end. A field that reads absent
   *  because its value was INADMISSIBLE is a different finding from one nothing
   *  stated, and the reviewer is owed the difference. */
  refused?: string[]
  basis: string
}

export interface IngestionStats {
  total: number
  new: number
  reviewed: number
  converted: number
  declined: number
  by_source: Record<string, number>
  in_box: number
  stretch: number
  out: number
}

export interface ConvertResult {
  // Note: toCamelCase remaps loan.property_name -> property, asset_type -> type, etc.
  loan: { id: string; property?: string } & Record<string, unknown>
  ingestion: Ingestion
}

/** Optional corrected/confirmed field values applied during conversion. */
export interface ConvertPayload {
  assignee?: string
  priority?: string
  reviewNotes?: string
  /** Override the thread guard: the thread already converted, but this really is a separate deal. */
  forceNewLoan?: boolean
  /** A person is pressing Create Deal: the server refuses (422 `required_fields_missing`)
   *  until the loan purpose, city + state, initial amount and fully funded amount
   *  are all there, naming each one that is not. Never set for the mail pipeline. */
  requireIntakeFields?: boolean
  overrides?: Partial<{
    property_name: string
    asset_type: string
    location: string
    borrower: string
    sponsor: string
    loan_amount: number
    ltv: number
    dscr: number
    // The four required at intake that are catalogue entries, not Loan columns.
    purpose: string
    city: string
    state: string
    fully_funded_amount: number
  }>
}

export interface InboundMailLedgerRow {
  id: number;
  folder: string | null;
  receivedAt: string | null;
  sender: string | null;
  subject: string | null;
  outcome: 'ingested' | 'duplicate' | 'rejected' | 'failed';
  ingestionId: string | null;
  error: string | null;
  attempts: number;
  processedAt: string | null;
}

export interface InboundMailStatus {
  enabled: boolean;
  mailbox: string;
  destinations: string[];
  intervalSeconds: number;
  poller: {
    startedAt: string | null;
    lastPollStartedAt: string | null;
    lastPollFinishedAt: string | null;
    lastCounts: Record<string, number> | null;
    lastError: string | null;
    consecutiveErrors: number;
    polls: number;
    /** A watched folder's listing is near the poller's scan cap, past which the newest
     *  mail is not listed: said on the Intake status strip in amber so a healthy strip
     *  never hides mail the poller did not see. Null when no folder is near the cap;
     *  absent from an older server. */
    listingWarning?: string | null;
  };
  mailboxSnapshot: {
    mailbox: string;
    folders: Record<string, { total?: number | null; unread?: number | null; error?: string }>;
  };
  ledgerTotals: Record<string, number>;
  recent: InboundMailLedgerRow[];
}

/** One message in the originations mailbox, as the Emailed Intake table reads
 *  it: when it arrived, who sent it, what it says, what rode along. */
export interface EmailedIntakeRow {
  id: string;
  receivedAt: string | null;
  sender: string | null;
  senderName: string | null;
  subject: string | null;
  attachments: { filename?: string; sizeKb?: number; parsed?: boolean }[];
  attachmentCount: number;
  status: 'new' | 'reviewed' | 'converted' | 'declined' | 'deferred';
  convertedToLoanId: string | null;
  propertyName: string | null;
  loanAmount: number | null;
  groupId: string | null;
  groupSize: number;
  groupRole: 'primary' | 'member' | null;
  duplicateOf: string | null;
  duplicateReason: 'identical' | 'thread' | 'property' | null;
  arrivedInJunk: boolean;
  junkSuspected: boolean;
  junkReason: string | null;
  /** Every reason the record is held out of the pipeline, first written first
   *  (an email listing several deals with a fragment for a name is held for both). */
  holdReasons?: string[];
  /** In the Intake strip's 'Supporting mail' tile — the same assignment. */
  supportingMail: boolean;
  /** The record standing for a deal the strip counts as waiting in intake. */
  waitingInIntake: boolean;
  /** The deal this row's group is on — null while it is not a deal. A group
   *  never spans two deals: mail that became a different deal (a killed deal
   *  and the live one its re-send made) is its own group. */
  groupLoanId: string | null;
  /** That deal was killed. */
  dealKilled: boolean;
}

export interface EmailedIntakeResponse {
  rows: EmailedIntakeRow[];
  counts: {
    total: number;
    supportingMail: number;
    waitingInIntake: number;
    /** Rows carrying duplicate evidence (identical bytes, or held as a re-send). */
    duplicates: number;
    junkSuspected: number;
    grouped: number;
  };
}

/** One analytic on the intake strip.
 *
 *  `ids` is the whole point: the tile reports the exact records it counted, and
 *  the screen filters to those ids rather than re-deriving the set from its own
 *  predicate. A figure and the rows behind it therefore cannot disagree — there
 *  is only one list, and the reviewer can always see where the number came from.
 */
export interface IntakeAnalytic {
  key: string;
  label: string;
  count: number;
  /** What this figure counts, in the reviewer's language. */
  basis: string;
  /** Which tab holds these records. */
  tab: 'queue' | 'emailed';
  ids: string[];
  /** Whether this tile partitions the headline. The strip draws only these
   *  in the grid; a cross-cutting figure is shown beside it. */
  partitions?: boolean;
}

/** One screen's count, and why it differs from the single intake number. */
export interface IntakeSurface {
  surface: string;
  count: number;
  difference: number;
  shows: string;
  /** Why this surface shows FEWER records than the intake total. */
  because: { reason: string; records: number; detail: string }[];
  /**
   * Why it shows MORE. The deal list carries rows intake never saw — entered by
   * hand, migrated, or created before the mailbox existed. A difference has two
   * directions and reporting only one leaves the other to be inferred.
   */
  adds?: { reason: string; records: number; detail: string }[];
  /** Of this surface's count, how many intake put there. Deal list only. */
  fromIntake?: number;
}

/** How many DEALS the intake records amount to — each once, however many
 *  emails describe it (Louis, 22 Sep: count by unique deal, not re-send). */
export interface IntakeUniqueDeals {
  count: number;
  /** Every live pipeline deal — the pipeline grid's own count. */
  onPipeline: number;
  /** Of those, the deals an intake record became. */
  fromIntake?: number;
  /** Of those, the deals entered by hand, migrated, or older than the mailbox. */
  notFromIntake?: number;
  inIntake: number;
  inIntakeAmount: number;
  inIntakeIds: string[];
  basis: string;
}

export interface IntakeReconciliation {
  partition: string;
  /** The single intake number for this environment. */
  total: number;
  uniqueDeals?: IntakeUniqueDeals;
  basis: string;
  analytics: IntakeAnalytic[];
  surfaces: IntakeSurface[];
  /** Whether the accounting tiles hold every record exactly once. */
  accounted: boolean;
}

export const ingestionService = {
  getQueue: () =>
    api.get('/ingestion/queue').then((r) => toCamelCase<Ingestion[]>(r.data)),
  // Originations mailbox poller: is it running, what is in the mailbox, what
  // happened to each message. See docs/originations-inbound-email.md.
  getInboundMailStatus: (limit = 50) =>
    api
      .get('/ingestion/inbound-email/status', { params: { limit } })
      .then((r) => toCamelCase<InboundMailStatus>(r.data)),
  /** Exactly these records, whatever their status.
   *
   *  An analytic reports the ids behind its figure and the screen asks for
   *  those records by id — rather than filtering a list endpoint that has its
   *  own predicate and cannot contain them all. */
  listByIds: (ids: string[]) =>
    ids.length === 0
      ? Promise.resolve([] as Ingestion[])
      : api
          .get('/ingestion/', { params: { ids: ids.join(',') } })
          .then((r) => toCamelCase<Ingestion[]>(r.data)),
  /** One intake number, the analytics behind it, and every stated difference. */
  getReconciliation: () =>
    api.get('/ingestion/reconciliation').then((r) => toCamelCase<IntakeReconciliation>(r.data)),
  // Emailed-intake mailbox triage (docs/originations-inbound-email.md).
  getEmailedIntake: (includeArchived = false) =>
    api
      .get('/ingestion/emailed', { params: { include_archived: includeArchived } })
      .then((r) => toCamelCase<EmailedIntakeResponse>(r.data)),
  /** Archive junk / duplicate mail out of the pipeline. Never deletes. */
  /** Re-read this record's originals and offer the differences for review.
   *  Writes no extracted value — only the proposal. */
  proposeReparse: (id: string) =>
    api.post(`/ingestion/${id}/reparse-propose`).then((r) => toCamelCase<{ proposed: number }>(r.data)),
  /** Run every fill on every promoted deal until a pass writes nothing:
   *  re-parse (accepting only what the engine alone ever decided), record →
   *  deal, the sponsor's model, blob figures, counterparty, derivation.
   *  Preview by default; `apply` writes, every value with lineage. A value a
   *  person decided is never touched and is listed under `needsPerson`. */
  reconcileDesk: (options: { apply?: boolean; ids?: string[]; limit?: number } = {}) =>
    api
      .post('/ingestion/reconcile-all', {
        apply: options.apply ?? false,
        ids: options.ids ?? null,
        limit: options.limit ?? 25,
      })
      // One BATCH, not the whole desk: the server answers within a time budget
      // and names what it did not start. `runReconciliation` drives the rest —
      // a single request long enough to cover the desk is one the platform's
      // ingress kills, and a killed request reaches the browser as a phantom
      // CORS failure rather than an error anyone can read.
      .then((r) => toCamelCase<ReconcileResponse>(r.data)),
  /** The most recent standing sweep — scheduled or manual — for a one-glance
   *  "is the desk current" check without running anything. */
  reconcileRunsLatest: () =>
    api
      .get('/ingestion/reconcile-runs/latest')
      .then((r) => toCamelCase<{ run: DeskReconciliationRun | null }>(r.data)),
  /** The standing sweep's history. */
  reconcileRunsHistory: (limit = 20) =>
    api
      .get('/ingestion/reconcile-runs', { params: { limit } })
      .then((r) => toCamelCase<{ runs: DeskReconciliationRun[] }>(r.data)),
  /** Sweep the desk now rather than waiting for the next scheduled tick. */
  reconcileRunNow: () =>
    api
      .post('/ingestion/reconcile-runs/run-now')
      .then((r) => toCamelCase<DeskReconciliationRun>(r.data)),
  /** Why every field on one deal is still empty — and whether a source states it. */
  gapCensus: (ingestionId: string, search = true) =>
    api
      .get(`/ingestion/${ingestionId}/gap-census`, { params: { search } })
      .then((r) => toCamelCase<GapCensus>(r.data)),
  /** The same over the desk. One BATCH per call, for the same reason
   *  `reconcileDesk` is: opening every stored source of every deal is minutes
   *  of work, and a request the ingress kills reaches the browser as a phantom
   *  CORS failure rather than an error anyone can read. `runGapCensus` drives
   *  the rest. */
  gapCensusDesk: (options: { ids?: string[]; limit?: number; search?: boolean } = {}) =>
    api
      .post('/ingestion/gap-census', {
        ids: options.ids ?? null,
        limit: options.limit ?? 25,
        search: options.search ?? true,
      })
      .then((r) => toCamelCase<GapCensusDesk>(r.data)),
  /** Re-parse the whole desk in one pass — proposals only, never a write. */
  proposeReparseAll: (limit = 50, refresh = false) =>
    api
      .post('/ingestion/reparse-propose-all', { limit, refresh })
      .then((r) => toCamelCase<{
        examined: number
        recordsWithProposals: number
        proposalsTotal: number
        skipped: Record<string, number>
        failed: { ingestionId: string; detail: string }[]
      }>(r.data)),
  /** Remove a message that was never a financing request, and the deal it made.
   *
   *  Deliberately not a kill: the kill reasons are a LOST-DEAL vocabulary, and
   *  junk was never an opportunity. The deal is archived so it leaves the
   *  pipeline without entering the funnel or kill-reason reporting. */
  removeNotADeal: (ids: string[], reason?: string) =>
    api.post('/ingestion/emailed/not-a-deal', { ids, reason }).then((r) => r.data),
  discardEmailed: (ids: string[], reason?: string) =>
    api.post('/ingestion/emailed/discard', { ids, reason }).then((r) => r.data),
  /** Declare several messages to be about one property. */
  reconcileEmailed: (primaryId: string, memberIds: string[]) =>
    api
      .post('/ingestion/emailed/reconcile', { primary_id: primaryId, member_ids: memberIds })
      .then((r) => r.data),
  /** Undo a wrong join. */
  splitEmailed: (ids: string[]) =>
    api.post('/ingestion/emailed/split', { ids }).then((r) => r.data),

  getAll: (params?: Record<string, string>) =>
    api.get('/ingestion/', { params }).then((r) => toCamelCase<Ingestion[]>(r.data)),

  getStats: () => api.get<IngestionStats>('/ingestion/stats').then((r) => r.data),

  getById: (id: string) =>
    api.get(`/ingestion/${id}`).then((r) => toCamelCase<Ingestion>(r.data)),

  /** Every intake record whose broker is waiting for a person's confirmation. */
  getBrokerReview: () =>
    api.get('/ingestion/broker-review').then((r) => toCamelCase<BrokerReviewQueue>(r.data)),

  /** Confirm the proposed broker on each selected record (a person's choice
   *  per record; the server confirms exactly what each record proposed). */
  confirmBrokerBatch: (ingestionIds: string[]) =>
    api
      .post('/ingestion/broker-review/confirm-batch', { ingestion_ids: ingestionIds })
      .then((r) => toCamelCase<BrokerBatchResult>(r.data)),

  /** Existing deals this record may be a re-upload of (register A8). Read-only:
   *  nothing is attached or merged — the operator decides attach vs create new
   *  before the record is committed. An empty `candidates` means no likely match. */
  getMatchPreview: (id: string) =>
    api.get(`/ingestion/${id}/match-preview`).then((r) => toCamelCase<MatchPreview>(r.data)),

  /** The deal dossier as this record has populated it, plus what still blocks
   *  processing. Computed on read from the stored extraction, so records
   *  ingested before this surface existed report correctly. */
  getDossier: (id: string) =>
    api.get(`/ingestion/${id}/dossier`).then((r) => toCamelCase<DossierCoverage>(r.data)),

  /** Re-run the stored originals through the current engine and diff against
   *  the stored extraction. Never persists — measures the engine only. */
  rerun: (id: string) =>
    api.post(`/ingestion/${id}/rerun`).then((r) => r.data as RerunResult),

  /** Register F6 — the internal missing-fields document. Served raw: the
   *  text body is the deliverable and must not be re-cased. Never sends. */
  getInfoRequest: (id: string, to?: string) =>
    api
      .get(`/ingestion/${id}/info-request`, { params: to ? { to } : undefined })
      .then((r) => r.data as InfoRequest),

  /** Upload a loan file/package — parsed and extracted server-side (no PII leaves the tenant). */
  uploadFile: (file: File, brokerName?: string) => {
    const form = new FormData()
    form.append('file', file)
    if (brokerName) form.append('broker_name', brokerName)
    return api
      .post('/ingestion/from-file', form, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => toCamelCase<Ingestion>(r.data))
  },

  /**
   * Deterministic multi-file intake — one record per request received. Email
   * containers each become their own ingestion (attachments included); loose
   * documents are partitioned by deal, joined only on positive evidence that
   * they describe the same property. `grouping` overrides that judgment.
   * Identical content re-uploads are deduped per group.
   */
  uploadPackage: (files: File[], brokerName?: string, grouping: GroupingMode = 'auto',
                  progressJob?: string) => {
    const form = new FormData()
    files.forEach((f) => form.append('files', f))
    if (brokerName) form.append('broker_name', brokerName)
    form.append('grouping', grouping)
    if (progressJob) form.append('progress_job', progressJob)
    return api
      .post('/ingestion/parse-files', form, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) =>
        toCamelCase<{ ingestions: UploadResult[] }>(r.data),
      )
  },

  /** Live stage snapshot for an in-flight parse — every stage reported was
   *  genuinely reached by the engine. */
  getParseProgress: (jobId: string) =>
    api.get(`/ingestion/parse-progress/${jobId}`).then((r) =>
      toCamelCase<ParseProgressSnapshot>(r.data)),

  /** Run (or fetch the cached) mapgen candidate-anchor sweep for a stored
   *  workbook — the minutes-long scan happens here, on demand, never at
   *  parse time. */
  generateOnboardingDraft: (id: string, sha256: string) =>
    api.post(`/ingestion/${id}/onboarding-draft/${sha256}`).then((r) =>
      toCamelCase<{
        filename: string
        candidates: number
        draft?: {
          /** The survey's own coverage: how much of this workbook the engine
           *  could read, and the labels it could not. Blindness used to be
           *  silent — a field was empty and nobody could tell whether the
           *  document failed to state it or the engine failed to read it. */
          blindSpots?: {
            labelledValuesSeen: number
            recognised: number
            unrecognised: number
            coverage: number
            unrecognisedLabels: Array<{ label: string; count: number }>
          }
        }
      }>(r.data)),

  /** Full deterministic evidence trail (envelope, provenance, conflicts, screens). */
  getParseReport: (id: string) =>
    api.get(`/ingestion/${id}/parse-report`).then((r) =>
      toCamelCase<{
        id: string
        contentSha256?: string
        emailEnvelope?: EmailEnvelope | null
        parseReport?: ParseReport | null
        screenResults?: ScreenResults | null
      }>(r.data),
    ),

  /** Archive one intake record (flagged, never deleted): it leaves every
   *  queue/list/metric view and releases its content hash so the same file
   *  can be re-ingested — the stale-record re-parse path. Audited. */
  archive: (id: string) =>
    api
      .post(`/ingestion/${id}/archive`)
      .then((r) => toCamelCase<{ id: string; archivedAt: string; archivedBy: string }>(r.data)),

  /** Declining a record also kills the deal intake promoted it into.
   *  `killReasonCode` names why; it defaults server-side to outside_mandate,
   *  and `reviewNotes` becomes the kill note. */
  review: (
    id: string,
    body: {
      status: string
      reviewNotes?: string
      convertedToLoanId?: string
      killReasonCode?: string
    },
  ) =>
    api
      .put(`/ingestion/${id}/review`, {
        status: body.status,
        review_notes: body.reviewNotes,
        converted_to_loan_id: body.convertedToLoanId,
        kill_reason_code: body.killReasonCode,
      })
      .then((r) => toCamelCase<Ingestion>(r.data)),

  /** Data Intelligence Review: record a human's decision on a flagged (low-confidence) deal
   *  name. Clears the flag, audits the decision, and mirrors onto a promoted
   *  loan if one exists. */
  resolveDealName: (
    id: string,
    propertyName: string,
    note?: string,
    /** The confirm-new-deal step: hold the deal this creates to the four fields
     *  required at intake, taking the person's answers as `overrides`. */
    intake?: { requireIntakeFields?: boolean; overrides?: ConvertPayload['overrides'] },
  ) =>
    api
      .put(`/ingestion/${id}/resolve-name`, {
        property_name: propertyName,
        note,
        require_intake_fields: intake?.requireIntakeFields ?? false,
        overrides: intake?.overrides ?? {},
      })
      .then((r) => toCamelCase<Ingestion>(r.data)),

  /** Point the engine at one or more datapoints. Read-only: it returns
   *  candidates with their exact location and the engine's case for each, and
   *  `searched` even when it finds nothing. Nothing is applied. */
  locate: (id: string, request: LocateInput) =>
    api
      .post(`/ingestion/${id}/locate`, {
        field: request.field,
        fields: request.fields,
        sha256: request.sha256,
        sheet: request.sheet,
        page_from: request.pageFrom,
        page_to: request.pageTo,
        cell: request.cell,
        line_no: request.lineNo,
      })
      .then((r) => toCamelCase<LocateResult>(r.data)),

  /** One-shot, atomic: creates the Loan, links it, and marks the ingestion
   *  converted. This is the person's approval that puts an emailed deal on
   *  the Pipeline: by default an emailed record waits for it (client ruling
   *  T-09; automatic creation is off unless an administrator turns it on in
   *  Settings > Intake). Idempotent — a record that is already a deal returns
   *  that deal rather than failing. */
  convert: (id: string, payload: ConvertPayload = {}) =>
    api
      .post(`/ingestion/${id}/convert`, {
        assignee: payload.assignee,
        priority: payload.priority,
        review_notes: payload.reviewNotes,
        overrides: payload.overrides ?? {},
        force_new_loan: payload.forceNewLoan ?? false,
        require_intake_fields: payload.requireIntakeFields ?? false,
      })
      .then((r) => toCamelCase<ConvertResult>(r.data)),

  /** Apply human-selected thread updates to the loan the thread converted to.
   *  Only parser-proposed fields are applicable; the write is audited. */
  /** Settle a thread message's waiting figures: `fields` are written to the
   *  deal, `keep` are declined (the deal keeps its value). Both audited. */
  applyToLoan: (id: string, fields: string[], reviewNotes?: string, keep: string[] = []) =>
    api
      .post(`/ingestion/${id}/apply-to-loan`, { fields, keep, review_notes: reviewNotes })
      .then((r) => toCamelCase<{ applied: Record<string, unknown>; loanUpdates: ThreadLoanUpdates }>(r.data)),

  /** Resolve a re-ingested record's field diff against the deal it matched by
   *  Control ID (register D1). Each `{ field, choice }` either writes the
   *  re-ingested value ('new') or keeps the deal's current one ('current'); the
   *  prior value is retained in the append-only lineage. */
  resolveReingest: (
    id: string,
    loanId: string,
    resolutions: { field: string; choice: 'new' | 'current' | 'clear' }[],
    reviewNotes?: string,
  ) =>
    api
      .post(`/ingestion/${id}/resolve-reingest`, {
        loan_id: loanId,
        resolutions,
        review_notes: reviewNotes,
      })
      .then((r) =>
        toCamelCase<{ applied: Record<string, unknown>; reingest: ReingestUpdates }>(r.data),
      ),

  /** One-off adoption of new workbook lines: bound to this deal only, flagged
   *  ad hoc, label-verified at apply time. Never touches the template. */
  modelChangeOneOff: (
    id: string,
    attachmentSha256: string,
    selections: BaselineDiffEntry[],
  ) =>
    api
      .post(`/ingestion/${id}/model-change/one-off`, {
        attachment_sha256: attachmentSha256,
        selections: selections.map((s) => ({
          metric: s.metric, sheet: s.sheet,
          label_cell: s.labelCell, value_cell: s.valueCell,
        })),
      })
      .then((r) => toCamelCase<Ingestion>(r.data)),

  /** Deterministic in-platform preview of a stored source file. */
  getSourcePreview: (id: string, sha256: string) =>
    api
      .get(`/ingestion/${id}/file/${sha256}/preview`)
      .then((r) => toCamelCase<SourcePreview>(r.data)),

  /** Raw stored bytes as a Blob (for the native PDF viewer / downloads). */
  getSourceBlob: (id: string, sha256: string) =>
    api
      .get(`/ingestion/${id}/file/${sha256}`, { responseType: 'blob' })
      .then((r) => r.data as Blob),

  /** Permanent adoption: creates the next template version as a DRAFT that
   *  must pass the governed approval chain before extraction trusts it. */
  modelChangeAdopt: (
    id: string,
    attachmentSha256: string,
    selections: BaselineDiffEntry[],
  ) =>
    api
      .post(`/ingestion/${id}/model-change/adopt`, {
        attachment_sha256: attachmentSha256,
        selections: selections.map((s) => ({
          metric: s.metric, sheet: s.sheet,
          label_cell: s.labelCell, value_cell: s.valueCell,
        })),
      })
      .then((r) =>
        toCamelCase<{ templateId: string; templateName: string; version: number; status: string; next: string }>(r.data),
      ),
}
